import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getMembers = createAsyncThunk(
    'members/getMembers',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching members with params:', params);

            const response = await apiClient.get(API_ENDPOINTS.MEMBERS, { params });
            console.log('Members response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Members retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch members',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get members error:', error);

            let errorMessage = 'Failed to fetch members';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch members';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getMember = createAsyncThunk(
    'members/getMember',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching member:', id);

            const response = await apiClient.get(API_ENDPOINTS.MEMBER(id));
            console.log('Member response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member retrieved successfully',
                    member: data?.member || null,
                    sms_history: data?.sms_history || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch member',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get member error:', error);

            let errorMessage = 'Failed to fetch member';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch member';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getMemberByUserId = createAsyncThunk(
    'members/getMemberByUserId',
    async (userId, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching member by user ID:', userId);

            const response = await apiClient.get(API_ENDPOINTS.MEMBER_BY_USER(userId));
            console.log('Member by user response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member retrieved successfully',
                    member: data?.member || null,
                    sms_history: data?.sms_history || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch member',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get member by user error:', error);

            let errorMessage = 'Failed to fetch member';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch member';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createMember = createAsyncThunk(
    'members/createMember',
    async (memberData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Creating member...');
            console.log('Member data:', memberData);

            const response = await apiClient.post(API_ENDPOINTS.CREATE_MEMBER, memberData);
            console.log('Create member response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Member created successfully',
                    member: data?.member || data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create member',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Create member error:', error);

            let errorMessage = 'Failed to create member';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create member';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateMember = createAsyncThunk(
    'members/updateMember',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating member:', id);
            console.log('Update data:', data);

            const response = await apiClient.post(API_ENDPOINTS.UPDATE_MEMBER(id), data);
            console.log('Update member response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member updated successfully',
                    member: responseData?.member || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update member',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update member error:', error);

            let errorMessage = 'Failed to update member';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update member';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteMember = createAsyncThunk(
    'members/deleteMember',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Deleting member:', id);

            const response = await apiClient.delete(API_ENDPOINTS.DELETE_MEMBER(id));
            console.log('Delete member response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete member',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Delete member error:', error);

            let errorMessage = 'Failed to delete member';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete member';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const bulkDeleteMembers = createAsyncThunk(
    'members/bulkDelete',
    async ({ member_ids }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Bulk deleting members:', member_ids);

            const response = await apiClient.post(API_ENDPOINTS.BULK_DELETE_MEMBERS, { ids: member_ids });
            console.log('Bulk delete members response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || `${member_ids.length} members deleted successfully`,
                    deleted_ids: data?.deleted_ids || member_ids
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete members',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Bulk delete members error:', error);

            let errorMessage = 'Failed to delete members';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete members';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const bulkActionMembers = createAsyncThunk(
    'members/bulkAction',
    async ({ member_ids, action, reason = null }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Bulk action on members:', { member_ids, action, reason });

            const response = await apiClient.post(API_ENDPOINTS.BULK_ACTION_MEMBERS, {
                member_ids: member_ids,
                action: action,
                reason: reason
            });
            console.log('Bulk action response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || `${member_ids.length} members processed`,
                    success: data?.success || [],
                    failed: data?.failed || [],
                    success_count: data?.success_count || 0,
                    failed_count: data?.failed_count || 0
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to process members',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Bulk action error:', error);

            let errorMessage = 'Failed to process members';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to process members';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const sendSmsToMember = createAsyncThunk(
    'members/sendSms',
    async ({ id, message, sender_id = null }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Sending SMS to member:', id);

            const response = await apiClient.post(API_ENDPOINTS.SEND_SMS_TO_MEMBER(id), {
                message: message,
                sender_id: sender_id
            });
            console.log('Send SMS response:', response.data);

            const { status, message: responseMessage, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: responseMessage || 'SMS sent successfully',
                    member_id: id,
                    sms_log: data?.sms_log || null
                };
            } else {
                return rejectWithValue({
                    message: responseMessage || 'Failed to send SMS',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Send SMS error:', error);

            let errorMessage = 'Failed to send SMS';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send SMS';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const bulkSendSms = createAsyncThunk(
    'members/bulkSendSms',
    async ({ member_ids, message, sender_id = null }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Bulk sending SMS to members:', member_ids);

            const response = await apiClient.post(API_ENDPOINTS.BULK_SEND_SMS, {
                member_ids: member_ids,
                message: message,
                sender_id: sender_id
            });
            console.log('Bulk send SMS response:', response.data);

            const { status, message: responseMessage, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: responseMessage || 'SMS sent successfully',
                    success: data?.success || [],
                    failed: data?.failed || [],
                    success_count: data?.success_count || 0,
                    failed_count: data?.failed_count || 0
                };
            } else {
                return rejectWithValue({
                    message: responseMessage || 'Failed to send SMS',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Bulk send SMS error:', error);

            let errorMessage = 'Failed to send SMS';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send SMS';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getMemberSmsLogs = createAsyncThunk(
    'members/getSmsLogs',
    async ({ id, per_page = 15, page = 1 }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching SMS logs for member:', id);

            const response = await apiClient.get(API_ENDPOINTS.MEMBER_SMS_LOGS(id), {
                params: { per_page, page }
            });
            console.log('SMS logs response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'SMS logs retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch SMS logs',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get SMS logs error:', error);

            let errorMessage = 'Failed to fetch SMS logs';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch SMS logs';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getSmsStats = createAsyncThunk(
    'members/getSmsStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching SMS statistics...');

            const response = await apiClient.get(API_ENDPOINTS.SMS_STATS, { params });
            console.log('SMS stats response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'SMS statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch SMS statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get SMS stats error:', error);

            let errorMessage = 'Failed to fetch SMS statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch SMS statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getMemberStats = createAsyncThunk(
    'members/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching member statistics...');

            const response = await apiClient.get(API_ENDPOINTS.MEMBER_STATS, { params });
            console.log('Member stats response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch member statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get member stats error:', error);

            let errorMessage = 'Failed to fetch member statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch member statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getMembersByGroup = createAsyncThunk(
    'members/getByGroup',
    async ({ group, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching members by group:', group);

            const response = await apiClient.get(API_ENDPOINTS.MEMBERS_BY_GROUP(group), { params });
            console.log('Members by group response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Members retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch members',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get members by group error:', error);

            let errorMessage = 'Failed to fetch members';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch members';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// =============================================
// IMPORT MEMBERS
// =============================================
export const importMembers = createAsyncThunk(
    'members/import',
    async ({ file, duplicate_handling = 'skip', dry_run = false }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const formData = new FormData();
            formData.append('file', file);
            formData.append('duplicate_handling', duplicate_handling);
            formData.append('dry_run', dry_run ? '1' : '0');

            console.log('📤 Importing members...', {
                duplicate_handling,
                dry_run: dry_run ? '1' : '0',
                dry_run_original: dry_run,
                dry_run_type: typeof dry_run
            });

            const response = await apiClient.post(API_ENDPOINTS.IMPORT_MEMBERS, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            console.log('📥 Import response:', response.data);
            console.log('📥 Import response status:', response.data.status);
            console.log('📥 Import response data:', response.data.data);
            console.log('📥 valid_data:', response.data.data?.valid_data);
            console.log('📥 invalid_data:', response.data.data?.invalid_data);
            console.log('📥 summary:', response.data.data?.summary);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Import completed successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to import members',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Import members error:', error);

            let errorMessage = 'Failed to import members';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to import members';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getImportHistory = createAsyncThunk(
    'members/getImportHistory',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching import history...');

            const response = await apiClient.get(API_ENDPOINTS.IMPORT_HISTORY, { params });
            console.log('Import history response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Import history retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch import history',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get import history error:', error);

            let errorMessage = 'Failed to fetch import history';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch import history';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const downloadTemplate = createAsyncThunk(
    'members/downloadTemplate',
    async (format = 'csv', { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Downloading template...');

            const response = await apiClient.get(API_ENDPOINTS.IMPORT_TEMPLATE, {
                params: { format },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `member_import_template.${format === 'csv' ? 'csv' : 'xlsx'}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return {
                success: true,
                message: 'Template downloaded successfully',
            };
        } catch (error) {
            console.error('Download template error:', error);

            let errorMessage = 'Failed to download template';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to download template';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// =============================================
// INITIAL STATE
// =============================================
const initialState = {
    members: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
        groups: []
    },
    currentMember: null,
    memberSmsLogs: [],
    smsLogsPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    smsStats: null,
    memberStats: null,
    importResult: null,
    importHistory: [],
    importHistoryPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

// =============================================
// SLICE
// =============================================
const memberSlice = createSlice({
    name: 'members',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearMemberState: (state) => {
            state.members = [];
            state.pagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0,
                groups: []
            };
            state.currentMember = null;
            state.memberSmsLogs = [];
            state.smsLogsPagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.smsStats = null;
            state.memberStats = null;
            state.importResult = null;
            state.importHistory = [];
            state.importHistoryPagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
        clearCurrentMember: (state) => {
            state.currentMember = null;
            state.memberSmsLogs = [];
        },
        clearMemberStats: (state) => {
            state.memberStats = null;
            state.smsStats = null;
        },
        clearImportResult: (state) => {
            state.importResult = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET MEMBERS =====
            .addCase(getMembers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getMembers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.members = responseData.data || [];
                state.pagination = {
                    current_page: responseData.meta?.current_page || 1,
                    last_page: responseData.meta?.last_page || 1,
                    per_page: responseData.meta?.per_page || 15,
                    total: responseData.meta?.total || 0,
                    groups: responseData.meta?.groups || []
                };
                state.successMessage = payload.message || 'Members retrieved successfully';
                state.apiMessage = payload.message || 'Members retrieved successfully';
                state.error = null;
            })
            .addCase(getMembers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch members';
                state.apiMessage = action.payload?.message || 'Failed to fetch members';
                state.successMessage = null;
                state.members = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0,
                    groups: []
                };
            })

            // ===== GET MEMBER =====
            .addCase(getMember.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getMember.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentMember = payload.member || null;
                state.memberSmsLogs = payload.sms_history || [];
                state.successMessage = payload.message || 'Member retrieved successfully';
                state.apiMessage = payload.message || 'Member retrieved successfully';
                state.error = null;
            })
            .addCase(getMember.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch member';
                state.apiMessage = action.payload?.message || 'Failed to fetch member';
                state.successMessage = null;
                state.currentMember = null;
                state.memberSmsLogs = [];
            })

            // ===== GET MEMBER BY USER ID =====
            .addCase(getMemberByUserId.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getMemberByUserId.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentMember = payload.member || null;
                state.memberSmsLogs = payload.sms_history || [];
                state.successMessage = payload.message || 'Member retrieved successfully';
                state.apiMessage = payload.message || 'Member retrieved successfully';
                state.error = null;
            })
            .addCase(getMemberByUserId.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch member';
                state.apiMessage = action.payload?.message || 'Failed to fetch member';
                state.successMessage = null;
                state.currentMember = null;
                state.memberSmsLogs = [];
            })

            // ===== CREATE MEMBER =====
            .addCase(createMember.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createMember.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Member created successfully';
                state.apiMessage = payload.message || 'Member created successfully';
                state.error = null;
                if (payload.member) {
                    state.members = [payload.member, ...state.members];
                }
            })
            .addCase(createMember.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create member';
                state.apiMessage = action.payload?.message || 'Failed to create member';
                state.successMessage = null;
            })

            // ===== UPDATE MEMBER =====
            .addCase(updateMember.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateMember.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Member updated successfully';
                state.apiMessage = payload.message || 'Member updated successfully';
                state.error = null;
                if (payload.member) {
                    state.members = state.members.map(m => 
                        m.id === payload.member.id ? payload.member : m
                    );
                    if (state.currentMember?.id === payload.member.id) {
                        state.currentMember = payload.member;
                    }
                }
            })
            .addCase(updateMember.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update member';
                state.apiMessage = action.payload?.message || 'Failed to update member';
                state.successMessage = null;
            })

            // ===== DELETE MEMBER =====
            .addCase(deleteMember.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteMember.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Member deleted successfully';
                state.apiMessage = payload.message || 'Member deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.members = state.members.filter(m => m.id !== payload.id);
                    if (state.currentMember?.id === payload.id) {
                        state.currentMember = null;
                        state.memberSmsLogs = [];
                    }
                }
            })
            .addCase(deleteMember.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete member';
                state.apiMessage = action.payload?.message || 'Failed to delete member';
                state.successMessage = null;
            })

            // ===== BULK DELETE MEMBERS =====
            .addCase(bulkDeleteMembers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(bulkDeleteMembers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Members deleted successfully';
                state.apiMessage = payload.message || 'Members deleted successfully';
                state.error = null;
                if (payload.deleted_ids) {
                    state.members = state.members.filter(m => !payload.deleted_ids.includes(m.id));
                }
            })
            .addCase(bulkDeleteMembers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete members';
                state.apiMessage = action.payload?.message || 'Failed to delete members';
                state.successMessage = null;
            })

            // ===== BULK ACTION MEMBERS =====
            .addCase(bulkActionMembers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(bulkActionMembers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Members processed successfully';
                state.apiMessage = payload.message || 'Members processed successfully';
                state.error = null;
            })
            .addCase(bulkActionMembers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to process members';
                state.apiMessage = action.payload?.message || 'Failed to process members';
                state.successMessage = null;
            })

            // ===== SEND SMS =====
            .addCase(sendSmsToMember.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendSmsToMember.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'SMS sent successfully';
                state.apiMessage = payload.message || 'SMS sent successfully';
                state.error = null;
            })
            .addCase(sendSmsToMember.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send SMS';
                state.apiMessage = action.payload?.message || 'Failed to send SMS';
                state.successMessage = null;
            })

            // ===== BULK SEND SMS =====
            .addCase(bulkSendSms.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(bulkSendSms.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'SMS sent successfully';
                state.apiMessage = payload.message || 'SMS sent successfully';
                state.error = null;
            })
            .addCase(bulkSendSms.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send SMS';
                state.apiMessage = action.payload?.message || 'Failed to send SMS';
                state.successMessage = null;
            })

            // ===== GET MEMBER SMS LOGS =====
            .addCase(getMemberSmsLogs.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getMemberSmsLogs.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.memberSmsLogs = responseData.data || [];
                state.smsLogsPagination = responseData.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = payload.message || 'SMS logs retrieved successfully';
                state.apiMessage = payload.message || 'SMS logs retrieved successfully';
                state.error = null;
            })
            .addCase(getMemberSmsLogs.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch SMS logs';
                state.apiMessage = action.payload?.message || 'Failed to fetch SMS logs';
                state.successMessage = null;
                state.memberSmsLogs = [];
                state.smsLogsPagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== GET SMS STATS =====
            .addCase(getSmsStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getSmsStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.smsStats = payload.stats || null;
                state.successMessage = payload.message || 'SMS statistics retrieved successfully';
                state.apiMessage = payload.message || 'SMS statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getSmsStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch SMS statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch SMS statistics';
                state.successMessage = null;
                state.smsStats = null;
            })

            // ===== GET MEMBER STATS =====
            .addCase(getMemberStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getMemberStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.memberStats = payload.stats || null;
                state.successMessage = payload.message || 'Member statistics retrieved successfully';
                state.apiMessage = payload.message || 'Member statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getMemberStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch member statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch member statistics';
                state.successMessage = null;
                state.memberStats = null;
            })

            // ===== GET MEMBERS BY GROUP =====
            .addCase(getMembersByGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getMembersByGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.members = responseData.members || [];
                state.successMessage = payload.message || 'Members retrieved successfully';
                state.apiMessage = payload.message || 'Members retrieved successfully';
                state.error = null;
            })
            .addCase(getMembersByGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch members';
                state.apiMessage = action.payload?.message || 'Failed to fetch members';
                state.successMessage = null;
            })

            // ===== IMPORT MEMBERS =====
            .addCase(importMembers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
                state.importResult = null;
            })
            .addCase(importMembers.fulfilled, (state, action) => {
                state.isLoading = false;
                state.importResult = action.payload;
                state.apiMessage = action.payload?.message || 'Import completed successfully';
                state.successMessage = action.payload?.message || 'Import completed successfully';
                state.error = null;
                
                console.log('✅ importMembers fulfilled:', {
                    payload: action.payload,
                    data: action.payload?.data,
                    valid_data: action.payload?.data?.valid_data,
                    summary: action.payload?.data?.summary
                });
            })
            .addCase(importMembers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to import members';
                state.apiMessage = action.payload?.message || 'Failed to import members';
                state.importResult = null;
                state.successMessage = null;
                
                console.error('❌ importMembers rejected:', action.payload);
            })

            // ===== GET IMPORT HISTORY - FIXED =====
            .addCase(getImportHistory.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getImportHistory.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                
                // The API response structure:
                // {
                //   current_page: 1,
                //   data: [...],        // The actual history items
                //   last_page: 4,
                //   per_page: 10,
                //   total: 34,
                //   ...
                // }
                
                // Store the history data (the array is inside responseData.data)
                state.importHistory = responseData.data || [];
                
                // Store the pagination data from the root of responseData
                // NOT from responseData.meta
                state.importHistoryPagination = {
                    current_page: responseData.current_page || 1,
                    last_page: responseData.last_page || 1,
                    per_page: responseData.per_page || 15,
                    total: responseData.total || 0
                };
                
                state.apiMessage = payload.message || 'Import history retrieved successfully';
                state.successMessage = payload.message || 'Import history retrieved successfully';
                state.error = null;
                
                console.log('📊 Import history stored:', {
                    historyCount: state.importHistory.length,
                    pagination: state.importHistoryPagination,
                    responseData: responseData
                });
            })
            .addCase(getImportHistory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch import history';
                state.apiMessage = action.payload?.message || 'Failed to fetch import history';
                state.importHistory = [];
                state.importHistoryPagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = null;
            })

            // ===== DOWNLOAD TEMPLATE =====
            .addCase(downloadTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(downloadTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload?.message || 'Template downloaded successfully';
                state.error = null;
            })
            .addCase(downloadTemplate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to download template';
                state.successMessage = null;
            });
    },
});

// =============================================
// EXPORT ACTIONS
// =============================================
export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearMemberState,
    clearCurrentMember,
    clearMemberStats,
    clearImportResult,
} = memberSlice.actions;

// =============================================
// EXPORT SELECTORS
// =============================================
export const selectMembers = (state) => state.members?.members || [];
export const selectMemberPagination = (state) => state.members?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
    groups: []
};
export const selectCurrentMember = (state) => state.members?.currentMember || null;
export const selectMemberSmsLogs = (state) => state.members?.memberSmsLogs || [];
export const selectSmsLogsPagination = (state) => state.members?.smsLogsPagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectSmsStats = (state) => state.members?.smsStats || null;
export const selectMemberStats = (state) => state.members?.memberStats || null;
export const selectMembersLoading = (state) => state.members?.isLoading || false;
export const selectMembersError = (state) => state.members?.error || null;
export const selectMembersSuccess = (state) => state.members?.successMessage || null;
export const selectMembersApiMessage = (state) => state.members?.apiMessage || null;
export const selectAvailableGroups = (state) => state.members?.pagination?.groups || [];

// Import selectors
export const selectImportHistory = (state) => state.members?.importHistory || [];
export const selectImportHistoryPagination = (state) => state.members?.importHistoryPagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectImportResult = (state) => state.members?.importResult || null;

// Summary selectors
export const selectMemberStatsSummary = (state) => {
    const stats = selectMemberStats(state);
    if (!stats) return null;
    return {
        total: stats.total || 0,
        active: stats.active || 0,
        inactive: stats.inactive || 0,
        visitors: stats.visitors || 0,
        groupDistribution: stats.group_distribution || []
    };
};

export const selectSmsStatsSummary = (state) => {
    const stats = selectSmsStats(state);
    if (!stats) return null;
    return {
        total: stats.total || 0,
        sent: stats.sent || 0,
        pending: stats.pending || 0,
        failed: stats.failed || 0,
        recentLogs: stats.recent_logs || [],
        smsByDate: stats.sms_by_date || []
    };
};

export default memberSlice.reducer;